import type { TFunction } from 'i18next';
import type { BreadcrumbLink, BreadcrumbRoute } from '@monecolier/ui';
import { NAV, type NavEntry } from './lib/navigation';

/**
 * UX-85 — fil d'Ariane de web-parent : pattern de route → maillons parents.
 * Mécanisme et choix d'architecture : voir packages/ui/src/Breadcrumbs.tsx ;
 * même règle de remplissage que apps/web-admin/src/breadcrumbs.ts (parents =
 * entrées de la Sidebar, `NAV` de lib/navigation.ts, jamais recopiés).
 *
 * Fonction de `t` et non constante : web-parent est bilingue, la config est
 * reconstruite à chaque changement de langue (App.tsx). Les libellés de page
 * réutilisent le `title` i18n que la page affiche déjà en `<h1>`.
 *
 * Pages d'authentification/d'accueil et d'acceptation d'invitation : absentes,
 * donc sans fil. Redirections (`/children`, `/supplies`…) : fil calculé sur la
 * destination, aucune entrée nécessaire.
 */
export function buildBreadcrumbRoutes(t: TFunction): BreadcrumbRoute[] {
  const link = (item: NavEntry): BreadcrumbLink => ({ to: item.to, label: t(item.labelKey) });

  return [
    // Inscriptions
    { path: '/children/new', parents: [link(NAV.inscriptions)], label: t('declareChild.title') },
    {
      path: '/children/:declaredChildId/guardians',
      parents: [link(NAV.inscriptions)],
      label: t('declaredChildGuardians.title'),
      formatLabel: (name) => t('breadcrumbs.childGuardians', { name }),
    },
    // Paiements
    {
      path: '/students/:studentId/financial-overview',
      parents: [link(NAV.payments)],
      label: t('studentFinancialOverview.title'),
      formatLabel: (name) => t('breadcrumbs.studentFinancialOverview', { name }),
    },
    // Documents
    { path: '/enrollments/:enrollmentId/documents', parents: [link(NAV.documents)], label: t('enrollmentDocuments.title') },
    // Réglages (hub UX-27)
    { path: '/profile', parents: [link(NAV.settings)], label: t('profile.title') },
    { path: '/notification-preferences', parents: [link(NAV.settings)], label: t('notificationPreferences.title') },
    { path: '/data-requests', parents: [link(NAV.settings)], label: t('dataRequests.title') },
    { path: '/change-password', parents: [link(NAV.settings)], label: t('changePassword.title') },
  ];
}
